import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

// Default types are protected — no X button shown for these
const DEFAULT_APARTMENT_TYPES = [
  'Studio', '1 Bedroom', '2 Bedroom', '3 Bedroom', '4 Bedroom', 'Penthouse',
  '1 Bedroom (Terrace)', '2 Bedroom (Terrace)', '3 Bedroom (Terrace)'
];

export default function ApartmentForm({ apartment, onSubmit, onCancel, isLoading }) {
  // Initialize apartment types from localStorage or use defaults
  const [apartmentTypes, setApartmentTypes] = useState(() => {
    try {
      const saved = localStorage.getItem('alrwid_apartment_types');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (e) {}
    return DEFAULT_APARTMENT_TYPES;
  });

  const apartmentDescription = apartment?.apartment_description || '';
  const isCustomDesc = apartmentDescription && !apartmentTypes.includes(apartmentDescription);
  const [useCustomType, setUseCustomType] = useState(isCustomDesc);
  const [customTypeValue, setCustomTypeValue] = useState(
    isCustomDesc ? apartmentDescription : ''
  );

  const defaultApartment = {
    unit_number: '',
    block: 'BLOCK A',
    floor: '1',
    direction: 'East',
    apartment_description: '1 Bedroom',
    area_sqm: 0,
    description: '',
    parking_spot: '',
    status: 'available',
    base_price: 0,
    currency: 'USD',
    bedrooms: 1,
    bathrooms: 1,
  };

  const [formData, setFormData] = useState(apartment ? { ...defaultApartment, ...apartment } : defaultApartment);

  const [useCustomCurrency, setUseCustomCurrency] = useState(
    apartment?.currency && !['USD', 'SAR', 'RWF', 'EUR'].includes(apartment.currency)
  );

  // Delete a custom type directly from the dropdown
  const deleteCustomType = (typeToDelete, e) => {
    // Prevent the Select from also selecting this item
    e.stopPropagation();
    e.preventDefault();

    const updatedTypes = apartmentTypes.filter(t => t !== typeToDelete);
    setApartmentTypes(updatedTypes);
    try {
      localStorage.setItem('alrwid_apartment_types', JSON.stringify(updatedTypes));
    } catch (err) {}

    // If the form is currently using this type, reset to a safe default
    if (formData.apartment_description === typeToDelete) {
      setFormData({ ...formData, apartment_description: '1 Bedroom' });
    }
    if (customTypeValue === typeToDelete) {
      setCustomTypeValue('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let finalType = formData.apartment_description;

    if (useCustomType && customTypeValue.trim()) {
      finalType = customTypeValue.trim();
      if (!apartmentTypes.includes(finalType)) {
        const updatedTypes = [...apartmentTypes, finalType];
        setApartmentTypes(updatedTypes);
        try {
          localStorage.setItem('alrwid_apartment_types', JSON.stringify(updatedTypes));
        } catch (err) {}
      }
    }

    onSubmit({ ...formData, apartment_description: finalType });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900">Basic Information</h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="unit_number">Unit Number *</Label>
            <Input
              id="unit_number"
              value={formData.unit_number}
              onChange={(e) => setFormData({...formData, unit_number: e.target.value})}
              placeholder="e.g., A101"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="block">Block</Label>
            <Select value={formData.block} onValueChange={(value) => setFormData({...formData, block: value})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="BLOCK A">BLOCK A</SelectItem>
                <SelectItem value="BLOCK B">BLOCK B</SelectItem>
                <SelectItem value="BLOCK C">BLOCK C</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="floor">Floor *</Label>
            <Select value={formData.floor} onValueChange={(value) => setFormData({...formData, floor: value})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="B">B (Basement)</SelectItem>
                <SelectItem value="G">G (Ground)</SelectItem>
                {[...Array(30)].map((_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="direction">Direction</Label>
            <Select value={formData.direction} onValueChange={(value) => setFormData({...formData, direction: value})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="East">East</SelectItem>
                <SelectItem value="West">West</SelectItem>
                <SelectItem value="North">North</SelectItem>
                <SelectItem value="South">South</SelectItem>
                <SelectItem value="East + West">East + West</SelectItem>
                <SelectItem value="North + South">North + South</SelectItem>
                <SelectItem value="East + North">East + North</SelectItem>
                <SelectItem value="West + South">West + South</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="parking_spot">Parking Spot</Label>
            <Input
              id="parking_spot"
              value={formData.parking_spot}
              onChange={(e) => setFormData({...formData, parking_spot: e.target.value})}
              placeholder="e.g., P-15"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="apartment_description">Apartment Type</Label>
            {!useCustomType ? (
              <Select
                value={formData.apartment_description || '1 Bedroom'}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    setUseCustomType(true);
                    setCustomTypeValue('');
                  } else {
                    setFormData({...formData, apartment_description: value});
                  }
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {apartmentTypes.map(type => {
                    const isCustom = !DEFAULT_APARTMENT_TYPES.includes(type);
                    return (
                      <SelectItem key={type} value={type} className="group">
                        <div className="flex items-center justify-between w-full gap-2">
                          <span className="flex-1 truncate">{type}</span>
                          {isCustom && (
                            <button
                              type="button"
                              onPointerDown={(e) => deleteCustomType(type, e)}
                              onClick={(e) => deleteCustomType(type, e)}
                              className="ml-1 p-0.5 rounded hover:bg-red-100 hover:text-red-600 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              title={`Delete "${type}"`}
                              aria-label={`Delete custom type ${type}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                  <SelectItem value="custom">Custom...</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={customTypeValue}
                  onChange={(e) => setCustomTypeValue(e.target.value)}
                  placeholder="e.g., 5 Bedroom"
                  autoFocus
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUseCustomType(false);
                    setCustomTypeValue('');
                  }}
                >
                  Select
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="area_sqm">Area (sqm) *</Label>
            <Input
              id="area_sqm"
              type="number"
              step="0.01"
              min="0"
              value={formData.area_sqm ?? ''}
              onChange={(e) => setFormData({...formData, area_sqm: parseFloat(e.target.value) || 0})}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bedrooms">Bedrooms</Label>
            <Input
              id="bedrooms"
              type="number"
              min="0"
              value={formData.bedrooms ?? ''}
              onChange={(e) => setFormData({...formData, bedrooms: parseInt(e.target.value) || 0})}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bathrooms">Bathrooms</Label>
            <Input
              id="bathrooms"
              type="number"
              step="0.5"
              min="0"
              value={formData.bathrooms ?? ''}
              onChange={(e) => setFormData({...formData, bathrooms: parseFloat(e.target.value) || 0})}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Detailed description of the apartment..."
            rows={3}
          />
        </div>
      </div>

      {/* Pricing & Status */}
      <div className="space-y-4 pt-4 border-t">
        <h3 className="font-semibold text-slate-900">Pricing & Status</h3>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="reserved">Reserved</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="base_price">Base Price</Label>
            <Input
              id="base_price"
              type="number"
              step="0.01"
              min="0"
              value={formData.base_price ?? ''}
              onChange={(e) => setFormData({...formData, base_price: parseFloat(e.target.value) || 0})}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            {!useCustomCurrency ? (
              <div className="flex gap-2">
                <Select value={formData.currency} onValueChange={(value) => {
                  if (value === 'custom') {
                    setUseCustomCurrency(true);
                    setFormData({...formData, currency: ''});
                  } else {
                    setFormData({...formData, currency: value});
                  }
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="SAR">SAR (Saudi Riyal)</SelectItem>
                    <SelectItem value="RWF">RWF (Rwandan Francs)</SelectItem>
                    <SelectItem value="EUR">EUR (Euro)</SelectItem>
                    <SelectItem value="custom">Custom...</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={formData.currency}
                  onChange={(e) => setFormData({...formData, currency: e.target.value.toUpperCase()})}
                  placeholder="e.g., GBP"
                  maxLength={10}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setUseCustomCurrency(false);
                    setFormData({...formData, currency: 'USD'});
                  }}
                >
                  Select
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600"
        >
          {isLoading ? 'Saving...' : apartment ? 'Update Apartment' : 'Create Apartment'}
        </Button>
      </div>
    </form>
  );
}